import React, { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "../components/ui/dialog";
import {
  Settings,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  Zap,
  Settings2,
  Cpu,
  Cable,
  Box,
  AlertTriangle,
  TestTube,
  Radio,
  Loader2,
  GripVertical,
  Database
} from "lucide-react";
import { toast } from "sonner";
import { TRANSFORMER_DATA } from "../data/transformerData";
import { SWITCHGEAR_DATA } from "../data/switchgearData";
import { MOTOR_DATA } from "../data/motorData";
import { CABLE_DATA } from "../data/cableData";

// Icon mapping
const ICON_OPTIONS = [
  { name: "Zap", icon: Zap, label: "Transformer/Power" },
  { name: "Settings2", icon: Settings2, label: "Switchgear/Settings" },
  { name: "Cpu", icon: Cpu, label: "Motor/Processor" },
  { name: "Cable", icon: Cable, label: "Cable" },
  { name: "Box", icon: Box, label: "Generic Box" }
];

const getIconComponent = (iconName) => {
  const iconOption = ICON_OPTIONS.find(opt => opt.name === iconName);
  return iconOption ? iconOption.icon : Box;
};

const COLOR_OPTIONS = [
  "#F59E0B", "#3B82F6", "#10B981", "#8B5CF6", "#EC4899",
  "#EF4444", "#06B6D4", "#84CC16", "#F97316", "#6366F1"
];

const AssetConfigPage = () => {
  const { token } = useAuth();
  const [assetTypes, setAssetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [expandedComponents, setExpandedComponents] = useState({});
  
  // Modals
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [showFailureModeModal, setShowFailureModeModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showMonitoringModal, setShowMonitoringModal] = useState(false);
  
  // Edit states
  const [editingAsset, setEditingAsset] = useState(null);
  const [editingComponent, setEditingComponent] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [currentComponentId, setCurrentComponentId] = useState(null);
  
  // Form states
  const [assetForm, setAssetForm] = useState({
    name: "", slug: "", description: "", icon: "Zap", color: "#3B82F6"
  });
  const [componentForm, setComponentForm] = useState({
    id: "", name: "", color: "#3B82F6"
  });
  const [failureModeForm, setFailureModeForm] = useState({
    name: "", description: "", importance: "", approach: "", severity: "Moderate",
    relatedTests: [], relatedMonitoring: []
  });
  const [testForm, setTestForm] = useState({
    name: "", description: "", importance: "", approach: "", 
    complexity: "Standard", cost: "$$", detectsFailures: []
  });
  const [monitoringForm, setMonitoringForm] = useState({
    name: "", description: "", importance: "", approach: "",
    complexity: "Standard", cost: "$$", detectsFailures: []
  });

  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    fetchAssetTypes();
  }, [token]);

  const fetchAssetTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/asset-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssetTypes(response.data);
      if (response.data.length > 0 && !selectedAsset) {
        setSelectedAsset(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to fetch asset types:", error);
      toast.error("Failed to load asset types");
    } finally {
      setLoading(false);
    }
  };

  // Check if any asset types have actual component data
  const hasComponentData = assetTypes.some(a => a.components && a.components.length > 0);

  const seedDefaultData = async () => {
    try {
      setSeeding(true);
      const seedPayload = [
        {
          slug: "transformers",
          name: "Transformers",
          description: "Power transformer diagnostics and monitoring",
          icon: "Zap",
          color: "#F59E0B",
          components: TRANSFORMER_DATA.map(c => ({
            id: c.id,
            name: c.name,
            color: c.color,
            failureModes: (c.failureModes || []).map(fm => ({
              name: fm.name, description: fm.description || "", importance: fm.importance || "",
              approach: fm.approach || "", severity: fm.severity || "Moderate",
              relatedTests: fm.relatedTests || [], relatedMonitoring: fm.relatedMonitoring || [],
              images: (fm.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            })),
            tests: (c.tests || []).map(t => ({
              name: t.name, description: t.description || "", importance: t.importance || "",
              approach: t.approach || "", complexity: t.complexity || "Standard", cost: t.cost || "$$",
              detectsFailures: t.detectsFailures || [],
              images: (t.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            })),
            monitoring: (c.monitoring || []).map(m => ({
              name: m.name, description: m.description || "", importance: m.importance || "",
              approach: m.approach || "", complexity: m.complexity || "Standard", cost: m.cost || "$$",
              detectsFailures: m.detectsFailures || [],
              images: (m.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            }))
          }))
        },
        {
          slug: "switchgear",
          name: "Switchgear",
          description: "Switchgear diagnostics and monitoring",
          icon: "Settings2",
          color: "#3B82F6",
          components: SWITCHGEAR_DATA.map(c => ({
            id: c.id,
            name: c.name,
            color: c.color,
            failureModes: (c.failureModes || []).map(fm => ({
              name: fm.name, description: fm.description || "", importance: fm.importance || "",
              approach: fm.approach || "", severity: fm.severity || "Moderate",
              relatedTests: fm.relatedTests || [], relatedMonitoring: fm.relatedMonitoring || [],
              images: (fm.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            })),
            tests: (c.tests || []).map(t => ({
              name: t.name, description: t.description || "", importance: t.importance || "",
              approach: t.approach || "", complexity: t.complexity || "Standard", cost: t.cost || "$$",
              detectsFailures: t.detectsFailures || [],
              images: (t.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            })),
            monitoring: (c.monitoring || []).map(m => ({
              name: m.name, description: m.description || "", importance: m.importance || "",
              approach: m.approach || "", complexity: m.complexity || "Standard", cost: m.cost || "$$",
              detectsFailures: m.detectsFailures || [],
              images: (m.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            }))
          }))
        },
        {
          slug: "motors",
          name: "Motors",
          description: "Motor diagnostics and monitoring",
          icon: "Cpu",
          color: "#10B981",
          components: MOTOR_DATA.map(c => ({
            id: c.id,
            name: c.name,
            color: c.color,
            failureModes: (c.failureModes || []).map(fm => ({
              name: fm.name, description: fm.description || "", importance: fm.importance || "",
              approach: fm.approach || "", severity: fm.severity || "Moderate",
              relatedTests: fm.relatedTests || [], relatedMonitoring: fm.relatedMonitoring || [],
              images: (fm.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            })),
            tests: (c.tests || []).map(t => ({
              name: t.name, description: t.description || "", importance: t.importance || "",
              approach: t.approach || "", complexity: t.complexity || "Standard", cost: t.cost || "$$",
              detectsFailures: t.detectsFailures || [],
              images: (t.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            })),
            monitoring: (c.monitoring || []).map(m => ({
              name: m.name, description: m.description || "", importance: m.importance || "",
              approach: m.approach || "", complexity: m.complexity || "Standard", cost: m.cost || "$$",
              detectsFailures: m.detectsFailures || [],
              images: (m.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            }))
          }))
        },
        {
          slug: "cables",
          name: "Cables",
          description: "Cable diagnostics and monitoring",
          icon: "Cable",
          color: "#8B5CF6",
          components: CABLE_DATA.map(c => ({
            id: c.id,
            name: c.name,
            color: c.color,
            failureModes: (c.failureModes || []).map(fm => ({
              name: fm.name, description: fm.description || "", importance: fm.importance || "",
              approach: fm.approach || "", severity: fm.severity || "Moderate",
              relatedTests: fm.relatedTests || [], relatedMonitoring: fm.relatedMonitoring || [],
              images: (fm.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            })),
            tests: (c.tests || []).map(t => ({
              name: t.name, description: t.description || "", importance: t.importance || "",
              approach: t.approach || "", complexity: t.complexity || "Standard", cost: t.cost || "$$",
              detectsFailures: t.detectsFailures || [],
              images: (t.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            })),
            monitoring: (c.monitoring || []).map(m => ({
              name: m.name, description: m.description || "", importance: m.importance || "",
              approach: m.approach || "", complexity: m.complexity || "Standard", cost: m.cost || "$$",
              detectsFailures: m.detectsFailures || [],
              images: (m.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            }))
          }))
        }
      ];

      await axios.post(`${API}/asset-types/seed-defaults`, seedPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Default asset data seeded successfully");
      await fetchAssetTypes();
    } catch (error) {
      console.error("Failed to seed data:", error);
      toast.error(error.response?.data?.detail || "Failed to seed default data");
    } finally {
      setSeeding(false);
    }
  };

  const migrateExistingData = async () => {
    try {
      setMigrating(true);
      // Build seed payload as fallback for asset types not in asset_configs
      const seedPayload = [
        {
          slug: "transformers", name: "Transformers", description: "Power transformer diagnostics and monitoring",
          icon: "Zap", color: "#F59E0B",
          components: TRANSFORMER_DATA.map(c => ({
            id: c.id, name: c.name, color: c.color,
            failureModes: (c.failureModes || []).map(fm => ({
              name: fm.name, description: fm.description || "", importance: fm.importance || "",
              approach: fm.approach || "", severity: fm.severity || "Moderate",
              relatedTests: fm.relatedTests || [], relatedMonitoring: fm.relatedMonitoring || [],
              images: (fm.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            })),
            tests: (c.tests || []).map(t => ({
              name: t.name, description: t.description || "", importance: t.importance || "",
              approach: t.approach || "", complexity: t.complexity || "Standard", cost: t.cost || "$$",
              detectsFailures: t.detectsFailures || [],
              images: (t.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            })),
            monitoring: (c.monitoring || []).map(m => ({
              name: m.name, description: m.description || "", importance: m.importance || "",
              approach: m.approach || "", complexity: m.complexity || "Standard", cost: m.cost || "$$",
              detectsFailures: m.detectsFailures || [],
              images: (m.images || []).map(img => ({ url: img.url, caption: img.caption || "" }))
            }))
          }))
        },
        {
          slug: "switchgear", name: "Switchgear", description: "Switchgear diagnostics and monitoring",
          icon: "Settings2", color: "#3B82F6",
          components: SWITCHGEAR_DATA.map(c => ({
            id: c.id, name: c.name, color: c.color,
            failureModes: (c.failureModes || []).map(fm => ({ name: fm.name, description: fm.description || "", importance: fm.importance || "", approach: fm.approach || "", severity: fm.severity || "Moderate", relatedTests: fm.relatedTests || [], relatedMonitoring: fm.relatedMonitoring || [], images: (fm.images || []).map(img => ({ url: img.url, caption: img.caption || "" })) })),
            tests: (c.tests || []).map(t => ({ name: t.name, description: t.description || "", importance: t.importance || "", approach: t.approach || "", complexity: t.complexity || "Standard", cost: t.cost || "$$", detectsFailures: t.detectsFailures || [], images: (t.images || []).map(img => ({ url: img.url, caption: img.caption || "" })) })),
            monitoring: (c.monitoring || []).map(m => ({ name: m.name, description: m.description || "", importance: m.importance || "", approach: m.approach || "", complexity: m.complexity || "Standard", cost: m.cost || "$$", detectsFailures: m.detectsFailures || [], images: (m.images || []).map(img => ({ url: img.url, caption: img.caption || "" })) }))
          }))
        },
        {
          slug: "motors", name: "Motors", description: "Motor diagnostics and monitoring",
          icon: "Cpu", color: "#10B981",
          components: MOTOR_DATA.map(c => ({
            id: c.id, name: c.name, color: c.color,
            failureModes: (c.failureModes || []).map(fm => ({ name: fm.name, description: fm.description || "", importance: fm.importance || "", approach: fm.approach || "", severity: fm.severity || "Moderate", relatedTests: fm.relatedTests || [], relatedMonitoring: fm.relatedMonitoring || [], images: (fm.images || []).map(img => ({ url: img.url, caption: img.caption || "" })) })),
            tests: (c.tests || []).map(t => ({ name: t.name, description: t.description || "", importance: t.importance || "", approach: t.approach || "", complexity: t.complexity || "Standard", cost: t.cost || "$$", detectsFailures: t.detectsFailures || [], images: (t.images || []).map(img => ({ url: img.url, caption: img.caption || "" })) })),
            monitoring: (c.monitoring || []).map(m => ({ name: m.name, description: m.description || "", importance: m.importance || "", approach: m.approach || "", complexity: m.complexity || "Standard", cost: m.cost || "$$", detectsFailures: m.detectsFailures || [], images: (m.images || []).map(img => ({ url: img.url, caption: img.caption || "" })) }))
          }))
        },
        {
          slug: "cables", name: "Cables", description: "Cable diagnostics and monitoring",
          icon: "Cable", color: "#8B5CF6",
          components: CABLE_DATA.map(c => ({
            id: c.id, name: c.name, color: c.color,
            failureModes: (c.failureModes || []).map(fm => ({ name: fm.name, description: fm.description || "", importance: fm.importance || "", approach: fm.approach || "", severity: fm.severity || "Moderate", relatedTests: fm.relatedTests || [], relatedMonitoring: fm.relatedMonitoring || [], images: (fm.images || []).map(img => ({ url: img.url, caption: img.caption || "" })) })),
            tests: (c.tests || []).map(t => ({ name: t.name, description: t.description || "", importance: t.importance || "", approach: t.approach || "", complexity: t.complexity || "Standard", cost: t.cost || "$$", detectsFailures: t.detectsFailures || [], images: (t.images || []).map(img => ({ url: img.url, caption: img.caption || "" })) })),
            monitoring: (c.monitoring || []).map(m => ({ name: m.name, description: m.description || "", importance: m.importance || "", approach: m.approach || "", complexity: m.complexity || "Standard", cost: m.cost || "$$", detectsFailures: m.detectsFailures || [], images: (m.images || []).map(img => ({ url: img.url, caption: img.caption || "" })) }))
          }))
        }
      ];

      const response = await axios.post(`${API}/asset-types/migrate-from-configs`, seedPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = response.data;
      toast.success(result.message);
      await fetchAssetTypes();
    } catch (error) {
      console.error("Migration failed:", error);
      toast.error(error.response?.data?.detail || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  // Asset Type CRUD
  const openAssetModal = (asset = null) => {
    if (asset) {
      setEditingAsset(asset);
      setAssetForm({
        name: asset.name,
        slug: asset.slug,
        description: asset.description || "",
        icon: asset.icon || "Zap",
        color: asset.color || "#3B82F6"
      });
    } else {
      setEditingAsset(null);
      setAssetForm({ name: "", slug: "", description: "", icon: "Zap", color: "#3B82F6" });
    }
    setShowAssetModal(true);
  };

  const saveAssetType = async () => {
    if (!assetForm.name || !assetForm.slug) {
      toast.error("Name and slug are required");
      return;
    }
    
    try {
      setSaving(true);
      if (editingAsset) {
        await axios.put(`${API}/asset-types/${editingAsset.slug}`, assetForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Asset type updated");
      } else {
        await axios.post(`${API}/asset-types`, {
          ...assetForm,
          components: []
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Asset type created");
      }
      setShowAssetModal(false);
      fetchAssetTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save asset type");
    } finally {
      setSaving(false);
    }
  };

  const deleteAssetType = async (slug) => {
    if (!confirm("Are you sure you want to delete this asset type?")) return;
    
    try {
      await axios.delete(`${API}/asset-types/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Asset type deleted");
      fetchAssetTypes();
      if (selectedAsset?.slug === slug) {
        setSelectedAsset(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete asset type");
    }
  };

  // Component CRUD
  const openComponentModal = (component = null) => {
    if (component) {
      setEditingComponent(component);
      setComponentForm({
        id: component.id,
        name: component.name,
        color: component.color || "#3B82F6"
      });
    } else {
      setEditingComponent(null);
      setComponentForm({ id: "", name: "", color: "#3B82F6" });
    }
    setShowComponentModal(true);
  };

  const saveComponent = async () => {
    if (!componentForm.name) {
      toast.error("Component name is required");
      return;
    }
    
    try {
      setSaving(true);
      const componentData = {
        id: componentForm.id || `comp-${Date.now()}`,
        name: componentForm.name,
        color: componentForm.color,
        failureModes: editingComponent?.failureModes || [],
        tests: editingComponent?.tests || [],
        monitoring: editingComponent?.monitoring || []
      };
      
      if (editingComponent) {
        await axios.put(
          `${API}/asset-types/${selectedAsset.slug}/components/${editingComponent.id}`,
          componentData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        toast.success("Component updated");
      } else {
        await axios.post(
          `${API}/asset-types/${selectedAsset.slug}/components`,
          componentData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        toast.success("Component added");
      }
      setShowComponentModal(false);
      fetchAssetTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save component");
    } finally {
      setSaving(false);
    }
  };

  const deleteComponent = async (componentId) => {
    if (!confirm("Delete this component and all its failure modes, tests, and monitoring options?")) return;
    
    try {
      await axios.delete(
        `${API}/asset-types/${selectedAsset.slug}/components/${componentId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Component deleted");
      fetchAssetTypes();
    } catch (error) {
      toast.error("Failed to delete component");
    }
  };

  // Failure Mode CRUD
  const openFailureModeModal = (componentId, failureMode = null) => {
    setCurrentComponentId(componentId);
    if (failureMode) {
      setEditingItem(failureMode);
      setFailureModeForm({ ...failureMode });
    } else {
      setEditingItem(null);
      setFailureModeForm({
        name: "", description: "", importance: "", approach: "", severity: "Moderate",
        relatedTests: [], relatedMonitoring: []
      });
    }
    setShowFailureModeModal(true);
  };

  const saveFailureMode = async () => {
    if (!failureModeForm.name) {
      toast.error("Failure mode name is required");
      return;
    }
    
    try {
      setSaving(true);
      const component = selectedAsset.components.find(c => c.id === currentComponentId);
      let failureModes = [...(component.failureModes || [])];
      
      if (editingItem) {
        const idx = failureModes.findIndex(fm => fm.name === editingItem.name);
        if (idx >= 0) failureModes[idx] = failureModeForm;
      } else {
        failureModes.push(failureModeForm);
      }
      
      await axios.put(
        `${API}/asset-types/${selectedAsset.slug}/components/${currentComponentId}`,
        { ...component, failureModes },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(editingItem ? "Failure mode updated" : "Failure mode added");
      setShowFailureModeModal(false);
      fetchAssetTypes();
    } catch (error) {
      toast.error("Failed to save failure mode");
    } finally {
      setSaving(false);
    }
  };

  const deleteFailureMode = async (componentId, failureModeName) => {
    if (!confirm("Delete this failure mode?")) return;
    
    try {
      const component = selectedAsset.components.find(c => c.id === componentId);
      const failureModes = component.failureModes.filter(fm => fm.name !== failureModeName);
      
      await axios.put(
        `${API}/asset-types/${selectedAsset.slug}/components/${componentId}`,
        { ...component, failureModes },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success("Failure mode deleted");
      fetchAssetTypes();
    } catch (error) {
      toast.error("Failed to delete failure mode");
    }
  };

  // Test CRUD
  const openTestModal = (componentId, test = null) => {
    setCurrentComponentId(componentId);
    if (test) {
      setEditingItem(test);
      setTestForm({ ...test });
    } else {
      setEditingItem(null);
      setTestForm({
        name: "", description: "", importance: "", approach: "",
        complexity: "Standard", cost: "$$", detectsFailures: []
      });
    }
    setShowTestModal(true);
  };

  const saveTest = async () => {
    if (!testForm.name) {
      toast.error("Test name is required");
      return;
    }
    
    try {
      setSaving(true);
      const component = selectedAsset.components.find(c => c.id === currentComponentId);
      let tests = [...(component.tests || [])];
      
      if (editingItem) {
        const idx = tests.findIndex(t => t.name === editingItem.name);
        if (idx >= 0) tests[idx] = testForm;
      } else {
        tests.push(testForm);
      }
      
      await axios.put(
        `${API}/asset-types/${selectedAsset.slug}/components/${currentComponentId}`,
        { ...component, tests },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(editingItem ? "Test updated" : "Test added");
      setShowTestModal(false);
      fetchAssetTypes();
    } catch (error) {
      toast.error("Failed to save test");
    } finally {
      setSaving(false);
    }
  };

  const deleteTest = async (componentId, testName) => {
    if (!confirm("Delete this test?")) return;
    
    try {
      const component = selectedAsset.components.find(c => c.id === componentId);
      const tests = component.tests.filter(t => t.name !== testName);
      
      await axios.put(
        `${API}/asset-types/${selectedAsset.slug}/components/${componentId}`,
        { ...component, tests },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success("Test deleted");
      fetchAssetTypes();
    } catch (error) {
      toast.error("Failed to delete test");
    }
  };

  // Monitoring CRUD
  const openMonitoringModal = (componentId, monitoring = null) => {
    setCurrentComponentId(componentId);
    if (monitoring) {
      setEditingItem(monitoring);
      setMonitoringForm({ ...monitoring });
    } else {
      setEditingItem(null);
      setMonitoringForm({
        name: "", description: "", importance: "", approach: "",
        complexity: "Standard", cost: "$$", detectsFailures: []
      });
    }
    setShowMonitoringModal(true);
  };

  const saveMonitoring = async () => {
    if (!monitoringForm.name) {
      toast.error("Monitoring option name is required");
      return;
    }
    
    try {
      setSaving(true);
      const component = selectedAsset.components.find(c => c.id === currentComponentId);
      let monitoring = [...(component.monitoring || [])];
      
      if (editingItem) {
        const idx = monitoring.findIndex(m => m.name === editingItem.name);
        if (idx >= 0) monitoring[idx] = monitoringForm;
      } else {
        monitoring.push(monitoringForm);
      }
      
      await axios.put(
        `${API}/asset-types/${selectedAsset.slug}/components/${currentComponentId}`,
        { ...component, monitoring },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(editingItem ? "Monitoring option updated" : "Monitoring option added");
      setShowMonitoringModal(false);
      fetchAssetTypes();
    } catch (error) {
      toast.error("Failed to save monitoring option");
    } finally {
      setSaving(false);
    }
  };

  const deleteMonitoring = async (componentId, monitoringName) => {
    if (!confirm("Delete this monitoring option?")) return;
    
    try {
      const component = selectedAsset.components.find(c => c.id === componentId);
      const monitoring = component.monitoring.filter(m => m.name !== monitoringName);
      
      await axios.put(
        `${API}/asset-types/${selectedAsset.slug}/components/${componentId}`,
        { ...component, monitoring },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success("Monitoring option deleted");
      fetchAssetTypes();
    } catch (error) {
      toast.error("Failed to delete monitoring option");
    }
  };

  const toggleComponentExpand = (componentId) => {
    setExpandedComponents(prev => ({
      ...prev,
      [componentId]: !prev[componentId]
    }));
  };

  // Update selectedAsset when assetTypes changes
  useEffect(() => {
    if (selectedAsset && assetTypes.length > 0) {
      const updated = assetTypes.find(a => a.slug === selectedAsset.slug);
      if (updated) setSelectedAsset(updated);
    }
  }, [assetTypes]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="asset-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Settings className="w-7 h-7 text-blue-600" />
            Asset Configuration
          </h1>
          <p className="text-slate-500 mt-1">
            Manage asset types, components, failure modes, tests, and monitoring options
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={migrateExistingData}
            disabled={migrating || seeding}
            variant={hasComponentData ? "outline" : "default"}
            className="gap-2"
            data-testid="migrate-data-btn"
          >
            {migrating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            {migrating ? "Migrating..." : "Migrate & Seed"}
          </Button>
          <Button
            onClick={seedDefaultData}
            disabled={seeding || migrating}
            variant="outline"
            className="gap-2 text-slate-600"
            data-testid="seed-defaults-btn"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {seeding ? "Seeding..." : "Overwrite with Defaults"}
          </Button>
          <Button onClick={() => openAssetModal()} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="add-asset-type-btn">
            <Plus className="w-4 h-4" />
            Add Asset Type
          </Button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Asset Types List */}
        <div className="w-64 flex-shrink-0">
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold text-slate-700 mb-3">Asset Types</h3>
            <div className="space-y-2">
              {assetTypes.map((asset) => {
                const IconComp = getIconComponent(asset.icon);
                return (
                  <div
                    key={asset.slug}
                    onClick={() => setSelectedAsset(asset)}
                    className={`p-3 rounded-lg cursor-pointer transition-all flex items-center gap-3 ${
                      selectedAsset?.slug === asset.slug
                        ? 'bg-blue-50 border-2 border-blue-500'
                        : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                    }`}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: asset.color + '20' }}
                    >
                      <IconComp className="w-4 h-4" style={{ color: asset.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{asset.name}</p>
                      <p className="text-xs text-slate-500">{asset.components?.length || 0} components</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Selected Asset Details */}
        <div className="flex-1 min-w-0">
          {selectedAsset ? (
            <div className="bg-white rounded-lg border">
              {/* Asset Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {(() => {
                    const IconComp = getIconComponent(selectedAsset.icon);
                    return (
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: selectedAsset.color + '20' }}
                      >
                        <IconComp className="w-5 h-5" style={{ color: selectedAsset.color }} />
                      </div>
                    );
                  })()}
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{selectedAsset.name}</h2>
                    <p className="text-sm text-slate-500">/{selectedAsset.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => openAssetModal(selectedAsset)}>
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  {!selectedAsset.isDefault && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => deleteAssetType(selectedAsset.slug)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Components */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-700">Components / Subsystems</h3>
                  <Button size="sm" onClick={() => openComponentModal()} className="gap-1">
                    <Plus className="w-4 h-4" />
                    Add Component
                  </Button>
                </div>

                {selectedAsset.components?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedAsset.components.map((component) => (
                      <div key={component.id} className="border rounded-lg">
                        {/* Component Header */}
                        <div
                          className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50"
                          onClick={() => toggleComponentExpand(component.id)}
                        >
                          <div
                            className="w-4 h-4 rounded-full flex-shrink-0"
                            style={{ backgroundColor: component.color }}
                          />
                          <span className="font-medium flex-1">{component.name}</span>
                          <span className="text-xs text-slate-500">
                            {component.failureModes?.length || 0} failures,{" "}
                            {component.tests?.length || 0} tests,{" "}
                            {component.monitoring?.length || 0} monitoring
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openComponentModal(component); }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={(e) => { e.stopPropagation(); deleteComponent(component.id); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          {expandedComponents[component.id] ? (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                        </div>

                        {/* Expanded Content */}
                        {expandedComponents[component.id] && (
                          <div className="border-t p-4 bg-slate-50 space-y-4">
                            {/* Failure Modes */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-red-700 flex items-center gap-2">
                                  <AlertTriangle className="w-4 h-4" />
                                  Failure Modes
                                </h4>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => openFailureModeModal(component.id)}
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add
                                </Button>
                              </div>
                              <div className="space-y-1">
                                {component.failureModes?.map((fm, idx) => (
                                  <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded text-sm">
                                    <span className="flex-1">{fm.name}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs ${
                                      fm.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                                      fm.severity === 'Moderate' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-green-100 text-green-700'
                                    }`}>
                                      {fm.severity}
                                    </span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => openFailureModeModal(component.id, fm)}
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-red-600"
                                      onClick={() => deleteFailureMode(component.id, fm.name)}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                                {(!component.failureModes || component.failureModes.length === 0) && (
                                  <p className="text-xs text-slate-500 italic">No failure modes defined</p>
                                )}
                              </div>
                            </div>

                            {/* Diagnostic Tests */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-blue-700 flex items-center gap-2">
                                  <TestTube className="w-4 h-4" />
                                  Diagnostic Tests
                                </h4>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => openTestModal(component.id)}
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add
                                </Button>
                              </div>
                              <div className="space-y-1">
                                {component.tests?.map((test, idx) => (
                                  <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded text-sm">
                                    <span className="flex-1">{test.name}</span>
                                    <span className="text-xs text-slate-500">{test.complexity}</span>
                                    <span className="text-xs text-slate-500">{test.cost}</span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => openTestModal(component.id, test)}
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-red-600"
                                      onClick={() => deleteTest(component.id, test.name)}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                                {(!component.tests || component.tests.length === 0) && (
                                  <p className="text-xs text-slate-500 italic">No tests defined</p>
                                )}
                              </div>
                            </div>

                            {/* Monitoring Options */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-green-700 flex items-center gap-2">
                                  <Radio className="w-4 h-4" />
                                  Online Monitoring
                                </h4>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => openMonitoringModal(component.id)}
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add
                                </Button>
                              </div>
                              <div className="space-y-1">
                                {component.monitoring?.map((mon, idx) => (
                                  <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded text-sm">
                                    <span className="flex-1">{mon.name}</span>
                                    <span className="text-xs text-slate-500">{mon.complexity}</span>
                                    <span className="text-xs text-slate-500">{mon.cost}</span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => openMonitoringModal(component.id, mon)}
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-red-600"
                                      onClick={() => deleteMonitoring(component.id, mon.name)}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                                {(!component.monitoring || component.monitoring.length === 0) && (
                                  <p className="text-xs text-slate-500 italic">No monitoring options defined</p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Box className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No components defined yet</p>
                    <Button className="mt-3" onClick={() => openComponentModal()}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add First Component
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border p-8 text-center text-slate-500">
              <Settings className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="text-lg">Select an asset type to configure</p>
            </div>
          )}
        </div>
      </div>

      {/* Asset Type Modal */}
      <Dialog open={showAssetModal} onOpenChange={setShowAssetModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingAsset ? "Edit Asset Type" : "Add Asset Type"}</DialogTitle>
            <DialogDescription>
              {editingAsset ? "Update asset type details" : "Create a new asset type for diagnostics"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={assetForm.name}
                  onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })}
                  placeholder="e.g., Transformers"
                />
              </div>
              <div>
                <Label>Slug *</Label>
                <Input
                  value={assetForm.slug}
                  onChange={(e) => setAssetForm({ ...assetForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="e.g., transformers"
                  disabled={!!editingAsset}
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={assetForm.description}
                onChange={(e) => setAssetForm({ ...assetForm, description: e.target.value })}
                placeholder="Brief description of this asset type"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icon</Label>
                <div className="flex gap-2 mt-1">
                  {ICON_OPTIONS.map((opt) => (
                    <button
                      key={opt.name}
                      onClick={() => setAssetForm({ ...assetForm, icon: opt.name })}
                      className={`p-2 rounded border ${
                        assetForm.icon === opt.name ? 'border-blue-500 bg-blue-50' : 'border-slate-200'
                      }`}
                      title={opt.label}
                    >
                      <opt.icon className="w-5 h-5" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color}
                      onClick={() => setAssetForm({ ...assetForm, color })}
                      className={`w-6 h-6 rounded ${
                        assetForm.color === color ? 'ring-2 ring-offset-1 ring-blue-500' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowAssetModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={saveAssetType} disabled={saving} className="flex-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingAsset ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Component Modal */}
      <Dialog open={showComponentModal} onOpenChange={setShowComponentModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{editingComponent ? "Edit Component" : "Add Component"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Component Name *</Label>
              <Input
                value={componentForm.name}
                onChange={(e) => setComponentForm({ ...componentForm, name: e.target.value })}
                placeholder="e.g., Windings, Bushings"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-1 mt-1 flex-wrap">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setComponentForm({ ...componentForm, color })}
                    className={`w-6 h-6 rounded ${
                      componentForm.color === color ? 'ring-2 ring-offset-1 ring-blue-500' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowComponentModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={saveComponent} disabled={saving} className="flex-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingComponent ? "Update" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Failure Mode Modal */}
      <Dialog open={showFailureModeModal} onOpenChange={setShowFailureModeModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Failure Mode" : "Add Failure Mode"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={failureModeForm.name}
                onChange={(e) => setFailureModeForm({ ...failureModeForm, name: e.target.value })}
                placeholder="e.g., Partial Discharge"
              />
            </div>
            <div>
              <Label>Severity</Label>
              <select
                value={failureModeForm.severity}
                onChange={(e) => setFailureModeForm({ ...failureModeForm, severity: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="Critical">Critical</option>
                <option value="Moderate">Moderate</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={failureModeForm.description}
                onChange={(e) => setFailureModeForm({ ...failureModeForm, description: e.target.value })}
                placeholder="Describe the failure mode..."
                rows={3}
              />
            </div>
            <div>
              <Label>Importance</Label>
              <Textarea
                value={failureModeForm.importance}
                onChange={(e) => setFailureModeForm({ ...failureModeForm, importance: e.target.value })}
                placeholder="Why is detecting this important?"
                rows={2}
              />
            </div>
            <div>
              <Label>Approach</Label>
              <Textarea
                value={failureModeForm.approach}
                onChange={(e) => setFailureModeForm({ ...failureModeForm, approach: e.target.value })}
                placeholder="How to investigate this failure mode..."
                rows={2}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowFailureModeModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={saveFailureMode} disabled={saving} className="flex-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingItem ? "Update" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Modal */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Diagnostic Test" : "Add Diagnostic Test"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Test Name *</Label>
              <Input
                value={testForm.name}
                onChange={(e) => setTestForm({ ...testForm, name: e.target.value })}
                placeholder="e.g., DGA - Dissolved Gas Analysis"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Complexity</Label>
                <select
                  value={testForm.complexity}
                  onChange={(e) => setTestForm({ ...testForm, complexity: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="Simple">Simple</option>
                  <option value="Standard">Standard</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
              <div>
                <Label>Cost</Label>
                <select
                  value={testForm.cost}
                  onChange={(e) => setTestForm({ ...testForm, cost: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="$">$ - Low</option>
                  <option value="$$">$$ - Medium</option>
                  <option value="$$$">$$$ - High</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={testForm.description}
                onChange={(e) => setTestForm({ ...testForm, description: e.target.value })}
                placeholder="Describe the test..."
                rows={3}
              />
            </div>
            <div>
              <Label>Importance</Label>
              <Textarea
                value={testForm.importance}
                onChange={(e) => setTestForm({ ...testForm, importance: e.target.value })}
                placeholder="Why is this test important?"
                rows={2}
              />
            </div>
            <div>
              <Label>Approach</Label>
              <Textarea
                value={testForm.approach}
                onChange={(e) => setTestForm({ ...testForm, approach: e.target.value })}
                placeholder="How to perform this test..."
                rows={2}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowTestModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={saveTest} disabled={saving} className="flex-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingItem ? "Update" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Monitoring Modal */}
      <Dialog open={showMonitoringModal} onOpenChange={setShowMonitoringModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Monitoring Option" : "Add Monitoring Option"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={monitoringForm.name}
                onChange={(e) => setMonitoringForm({ ...monitoringForm, name: e.target.value })}
                placeholder="e.g., Online DGA Monitor"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Complexity</Label>
                <select
                  value={monitoringForm.complexity}
                  onChange={(e) => setMonitoringForm({ ...monitoringForm, complexity: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="Simple">Simple</option>
                  <option value="Standard">Standard</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
              <div>
                <Label>Cost</Label>
                <select
                  value={monitoringForm.cost}
                  onChange={(e) => setMonitoringForm({ ...monitoringForm, cost: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="$">$ - Low</option>
                  <option value="$$">$$ - Medium</option>
                  <option value="$$$">$$$ - High</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={monitoringForm.description}
                onChange={(e) => setMonitoringForm({ ...monitoringForm, description: e.target.value })}
                placeholder="Describe the monitoring system..."
                rows={3}
              />
            </div>
            <div>
              <Label>Importance</Label>
              <Textarea
                value={monitoringForm.importance}
                onChange={(e) => setMonitoringForm({ ...monitoringForm, importance: e.target.value })}
                placeholder="Why is this monitoring important?"
                rows={2}
              />
            </div>
            <div>
              <Label>Approach</Label>
              <Textarea
                value={monitoringForm.approach}
                onChange={(e) => setMonitoringForm({ ...monitoringForm, approach: e.target.value })}
                placeholder="How to implement this monitoring..."
                rows={2}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowMonitoringModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={saveMonitoring} disabled={saving} className="flex-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingItem ? "Update" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetConfigPage;
